import {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { computeKeyboardOverlayStyles, glideTopFloorPx } from '../../components/common/keyboardOverlayStyles';
import { foundation } from '../../ui/tokens';
import { canPredictKeyboard } from '../../utils/ui/keyboardPresence';
import { panelHasInnerScroller } from '../../utils/ui/modalRevealScroller';
import { kbDebugEnabled, kbLog } from '../../utils/ui/keyboardReframeDebug';
import { KB_MOTION_MS } from './modalCloseChoreography';

/**
 * Shape-based keyboard handling for `AnimatedModalShell` — extracted
 * 2026-08-20, round 3 (500-line cap; same precedent as
 * `useKeyboardFieldReveal.js`). Owns everything needed to decide HOW the
 * modal reacts to the iOS keyboard opening: the resting-rect measurement,
 * the close-hold, the glide/curtain computation, and the curtain
 * two-phase start (P1 fix below). `AnimatedModalShell` just spreads the
 * returned styles.
 *
 * SHAPE-BASED RULE (founder decision, 2026-08-20): the box NEVER animates
 * its position mid-keyboard-slide via padding/height tweening — that read
 * as a "glitch" on device (the instant-clamp build this replaced). Instead
 * the shell picks ONE of two native-feeling modes automatically, by
 * measuring whether the dialog's resting height fits above the keyboard —
 * see `keyboardOverlayStyles.js` for the pure `'glide'`/`'curtain'` math.
 *
 * P1 fix (round 3): CSS `max-height` interpolates FROM whatever value it
 * previously held — for a caller with an existing cap (e.g. ConfirmDialog's
 * ~78vh) that's mostly dead time then a too-fast tail, and for a caller with
 * NO maxHeight at all it's `none -> px`, which is NON-interpolable and snaps
 * instantly — exactly the "modal shrinks" behavior the founder rejected on
 * device. Fix: the FIRST render after `mode` becomes `'curtain'` shows
 * `maxHeight: restingRect.height` (a visual no-op — it's the box's own
 * natural height) with `transition:'none'`; a double-`requestAnimationFrame`
 * later flips to the real clamp + the animated `max-height` transition, so
 * the shrink always interpolates FROM the true resting height, never from a
 * stale cap or from `none`. Reduced-motion callers settle immediately
 * (no rAF wait, no animated shrink either — see the effect below).
 */

/**
 * How long the keyboard geometry (frame + resting rect) lingers after
 * focusout before clearing. iOS starts the dismiss animation well after
 * focusout (device logs: restored viewport reported 400–750ms later);
 * clearing immediately would grow/glide the modal back BEFORE the keyboard's
 * own slide instead of overlapping it (still occluded, so invisible).
 */
const KB_CLOSE_LINGER_MS = 140;

/** Curtain grow-back: after the hold, the scrim pin outlives the dialog clamp
 *  for the keyboard motion (+80 safety) so the card grows DOWN from its top. */
const KB_GROW_BACK_MS = KB_MOTION_MS + 80;

/**
 * @param {{
 *   viewportFrame: {keyboardOpen:boolean, offsetTop:number, height:number, obscured:number},
 *   dialogEl: HTMLElement|null,
 *   keyboardAlign: 'center'|'end',
 *   enterMorphActive: boolean,
 *   enterMorphSettling: boolean,
 *   reducedMotion: boolean,
 *   rest?: boolean,
 * }} args
 * @returns {{keyboardMode: 'glide'|'curtain'|'rest'|null, keyboardScrimStyle: object|null, keyboardDialogStyle: object|null}}
 *
 * `rest` (KAN-930, Option B — founder decision 2026-10-22): the card already
 * RESTS under the header (`useModalTypingRest`). Mode `'rest'` returns NO
 * styles — no curtain clamp, no close-hold geometry, no grow-back pin — the
 * body shrink (`modalBodyShrink`) keeps the field and footer above the keys.
 */
export default function useModalKeyboardMode({
  viewportFrame, dialogEl, keyboardAlign, enterMorphActive, enterMorphSettling, reducedMotion, rest = false,
}) {
  // ── Resting-rect capture ────────────────────────────────────────────────
  // `restingRect` is measured once per keyboard-open, from the dialog's
  // on-screen position just before the keyboard appears. Cleared by the
  // close-hold below, NOT on the keyboardOpen flip — it must survive the
  // hold window so the glide-back / curtain grow-back has a stable anchor
  // for its final frame.
  const [restingRect, setRestingRect] = useState(null);

  // ── Close-hold: keep the OPEN geometry through a 140ms window after the
  // flip — computed SYNCHRONOUSLY at render time, via refs, NOT via
  // effect-driven state. `keyboardOpen` flips false at focusout, but iOS
  // starts sliding the keyboard away LATER (device logs: the restored
  // viewport reports 400–750ms after focusout). Clearing the geometry
  // immediately would glide/grow the modal back BEFORE the keyboard's own
  // slide instead of overlapping it — still occluded by the (still-visible)
  // keyboard either way, so holding it briefly costs nothing and avoids a
  // second, separate motion.
  //
  // Round 4 fix (2026-08-20, log-proven): the ORIGINAL version toggled a
  // `kbLinger` STATE flag inside a `useEffect`. Effects run AFTER the render
  // that triggered them, so there was exactly one "gap render" — the render
  // where `viewportFrame.keyboardOpen` had already flipped false but the
  // effect hadn't yet flipped `kbLinger` true — where `effectiveFrame`
  // resolved to the raw (closed) frame. That gap render made `keyboardMode`
  // momentarily null, which reset `curtainSettled` (below) to false; the
  // VERY NEXT render (once the effect caught up) saw curtain mode again with
  // a freshly-reset phase, RESTARTING the two-phase sequence mid-hold. Device
  // log: `open=0 mode=- (gap) → open=1 clamp=439 (restarted) → clamp=267` —
  // visible as grow → re-shrink → grow. Fixed by making the hold itself pure
  // render-time bookkeeping (refs, mutated inline — no effect, no state, no
  // lag) so NO render between the flip and the hold's expiry can ever
  // observe a bare `keyboardOpen=false`.
  const lastOpenRef = useRef(null); // { frame, restingRect } snapshot while open
  const closeFlipAtRef = useRef(null); // ms timestamp of the observed close flip
  const curtainSessionRef = useRef(false); // last render was curtain (and animated)
  const [growBackTop, setGrowBackTop] = useState(null); // pin top during grow-back

  if (viewportFrame.keyboardOpen) {
    lastOpenRef.current = { frame: viewportFrame, restingRect };
    closeFlipAtRef.current = null;
  } else if (lastOpenRef.current && closeFlipAtRef.current === null) {
    // First render to observe the flip — stamp it NOW, not in an effect.
    closeFlipAtRef.current = Date.now();
  }

  const holdActive = !viewportFrame.keyboardOpen
    && lastOpenRef.current != null
    && closeFlipAtRef.current != null
    && (Date.now() - closeFlipAtRef.current) < KB_CLOSE_LINGER_MS;

  const effectiveFrame = holdActive ? lastOpenRef.current.frame : viewportFrame;
  const effectiveRestingRect = holdActive ? lastOpenRef.current.restingRect : restingRect;

  // The TIMER that ends the hold is still effect-driven — that's fine, since
  // the flip itself already committed synchronously above; nothing depends
  // on this effect having run yet. It fires once, clears the held geometry
  // AND the resting-rect state TOGETHER (same tick, refs mutated before the
  // state update lands), so the hold's expiry is also exactly one render —
  // never a frame where refs are stale but state has already moved, or the
  // reverse.
  useEffect(() => {
    if (viewportFrame.keyboardOpen) return undefined;
    if (!lastOpenRef.current) return undefined;
    const t = setTimeout(() => {
      const pinTop = lastOpenRef.current.restingRect
        ? lastOpenRef.current.restingRect.top : null;
      if (curtainSessionRef.current && pinTop != null) setGrowBackTop(pinTop);
      lastOpenRef.current = null;
      closeFlipAtRef.current = null;
      setRestingRect(null);
    }, KB_CLOSE_LINGER_MS);
    return () => clearTimeout(t);
  }, [viewportFrame.keyboardOpen]);

  // Grow-back timer: a new keyboard open cancels it (fresh session below).
  useEffect(() => {
    if (growBackTop == null) return undefined;
    if (viewportFrame.keyboardOpen) {
      setGrowBackTop(null);
      return undefined;
    }
    const t = setTimeout(() => setGrowBackTop(null), KB_GROW_BACK_MS);
    return () => clearTimeout(t);
  }, [growBackTop, viewportFrame.keyboardOpen]);

  // Measure the dialog's RESTING position once, right as the keyboard opens,
  // so the glide/curtain math below has a stable anchor. OFFSET geometry on
  // purpose, not getBoundingClientRect(): the standard mobile enter starts
  // the panel at translate3d(0, 24px, 0) for 280ms, and an autoFocus dialog
  // (every type-to-confirm) opens keyboard-up INSIDE that window — a client
  // rect would read the live transform and pin the box 24px low for the whole
  // keyboard session. offsetTop/offsetHeight ignore transforms, and the fixed
  // scrim (viewport top 0, borderless) is the offsetParent, so they ARE the
  // resting layout position even mid-slide — and they're equally
  // immune to iOS's transient document pan on focus (fixed scrim never moves
  // with document scroll; do not "compensate" with window.scrollY).
  // FLIP morphs are still skipped: their spring rewrites layout via transform
  // from an arbitrary source rect, so we wait for `enterSettled` (via the
  // `enterMorphSettling` dep flipping false) and measure then. Clearing on
  // keyboard-close lives in the close-hold logic above (NOT here) so the
  // rect survives the hold window.
  useLayoutEffect(() => {
    if (!viewportFrame.keyboardOpen) return;
    if (!dialogEl || restingRect != null) return;
    if (enterMorphActive || enterMorphSettling) return;
    setRestingRect({ top: dialogEl.offsetTop, height: dialogEl.offsetHeight });
    // kbdebug: `h` vs `ch` (box height vs content height) tells stretched-box
    // (h ≫ ch: a sizing rule inflating the card) apart from real content in
    // one pasted line — the delete-confirm capture measured h=544 for ~280px
    // of visible content and the cause was not derivable offline.
    kbLog('rest', {
      top: dialogEl.offsetTop,
      h: dialogEl.offsetHeight,
      ch: dialogEl.scrollHeight,
    });
  }, [
    viewportFrame.keyboardOpen, dialogEl, restingRect,
    enterMorphActive, enterMorphSettling,
  ]);

  const sp = foundation.spacePx;
  const shaped = computeKeyboardOverlayStyles(
    {
      keyboardOpen: effectiveFrame.keyboardOpen,
      offsetTop: effectiveFrame.offsetTop,
      height: effectiveFrame.height,
      obscured: effectiveFrame.obscured,
    },
    effectiveRestingRect,
    sp,
    keyboardAlign,
    glideTopFloorPx(),
  );
  const rawResult = (rest && shaped.mode) ? { mode: 'rest', scrim: null, dialog: null } : shaped;
  const keyboardMode = rawResult.mode;
  curtainSessionRef.current = keyboardMode === 'curtain' && !reducedMotion;
  // Grow-back: pin only (no clamp) so the released max-height grows downward.
  const growBackActive = growBackTop != null && !viewportFrame.keyboardOpen && keyboardMode == null;
  const keyboardScrimStyle = growBackActive
    ? { alignItems: 'flex-start', paddingTop: `${growBackTop}px` }
    : rawResult.scrim;

  // ── P1: curtain two-phase start ─────────────────────────────────────────
  // See the P1 comment above. `curtainSettled` starts false every time `mode`
  // (freshly) becomes `'curtain'`, and a double-rAF flips it true — one frame
  // to let the phase-1 (resting-height, transition:none) style actually
  // paint before the browser has anything to interpolate FROM. Reduced-motion
  // callers skip the wait (and the animated shrink) entirely. Resets to false
  // whenever `mode` leaves `'curtain'`, so the next curtain entry restarts
  // the two-phase sequence instead of reusing a stale settled flag.
  const [curtainSettled, setCurtainSettled] = useState(false);
  useEffect(() => {
    if (keyboardMode !== 'curtain') {
      setCurtainSettled(false);
      return undefined;
    }
    if (reducedMotion) {
      setCurtainSettled(true);
      return undefined;
    }
    setCurtainSettled(false);
    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) setCurtainSettled(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [keyboardMode, reducedMotion]);

  let keyboardDialogStyle = rawResult.dialog;
  if (keyboardMode === 'curtain') {
    keyboardDialogStyle = curtainSettled
      ? rawResult.dialog
      : {
        maxHeight: `${effectiveRestingRect.height}px`,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      };
  }

  // Safety net: the PANEL must never stay scrolled while its body has its own
  // scroller (curtain makes the panel overflow:auto; iOS can also scroll it
  // natively when a pinned-band field focuses → title cut, footer mid-card).
  // Touch only (canPredictKeyboard), never on mount: runs on keyboard/mode
  // flips while a keyboard is up or a mode is active, and on a trusted focusin.
  // A hug-content panel (no inner scroller) legitimately scrolls itself.
  const keyboardOpenNow = viewportFrame.keyboardOpen;
  useEffect(() => {
    if (!dialogEl || typeof dialogEl.addEventListener !== 'function') return undefined;
    const reset = () => {
      if (!dialogEl.scrollTop || !canPredictKeyboard()) return;
      if (panelHasInnerScroller(dialogEl)) dialogEl.scrollTop = 0;
    };
    const onFocusIn = (e) => { if (e.isTrusted) reset(); };
    dialogEl.addEventListener('focusin', onFocusIn);
    let scrollOn = false;
    if (keyboardOpenNow || keyboardMode) {
      reset();
      if (keyboardOpenNow && canPredictKeyboard() && panelHasInnerScroller(dialogEl)) {
        dialogEl.addEventListener('scroll', reset, { passive: true });
        scrollOn = true;
      }
    }
    return () => {
      dialogEl.removeEventListener('focusin', onFocusIn);
      if (scrollOn) dialogEl.removeEventListener('scroll', reset);
    };
  }, [dialogEl, keyboardOpenNow, keyboardMode]);

  // kbdebug: the glide/curtain styles the shell is about to apply for this
  // frame — based on effectiveFrame/effectiveRestingRect (the hold-held
  // values), matching what actually renders.
  const clampForLog = (keyboardDialogStyle && keyboardDialogStyle.maxHeight)
    ? parseInt(keyboardDialogStyle.maxHeight, 10)
    : -1;
  useEffect(() => {
    if (!kbDebugEnabled()) return;
    kbLog('pad', {
      open: effectiveFrame.keyboardOpen ? 1 : 0,
      ot: effectiveFrame.offsetTop,
      h: effectiveFrame.height,
      obs: effectiveFrame.obscured,
      pinTop: effectiveRestingRect ? effectiveRestingRect.top : -1,
      clamp: clampForLog,
      mode: keyboardMode || '-',
    });
  }, [
    effectiveFrame.keyboardOpen, effectiveFrame.offsetTop, effectiveFrame.height,
    effectiveFrame.obscured, effectiveRestingRect, keyboardMode, clampForLog,
  ]);

  // Curtain's first frame: the shell drops ONLY the max-height entry for it.
  const curtainPhase1 = keyboardMode === 'curtain' && !curtainSettled && !reducedMotion;
  return { keyboardMode, keyboardScrimStyle, keyboardDialogStyle, curtainPhase1 };
}
